// @flow
import { useEffect } from 'react';

export function useFocusLoop(
  enabled: boolean,
  rootRef: $ReadOnly<{ current: ?Element }>,
): void {
  useEffect(() => {
    if (!enabled) return undefined;
    const root = rootRef.current;
    if (!root) return undefined;

    const F =
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])';

    const visible = (el: Element): boolean => {
      const style = window.getComputedStyle(el);
      if (style.visibility === 'hidden' || style.display === 'none')
        return false;
      const rect = el.getBoundingClientRect();
      return !(rect.width === 0 && rect.height === 0);
    };

    const list = (): Array<HTMLElement> =>
      Array.from(root.querySelectorAll(F)).filter(
        el => el instanceof HTMLElement && visible(el),
      );

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key !== 'Tab') return;
      const els = list();
      if (els.length === 0) return;

      const first = els[0];
      const last = els[els.length - 1];
      const active: Element | null = document.activeElement;

      if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      }
    }

    root.addEventListener('keydown', onKeyDown);
    return () => {
      root.removeEventListener('keydown', onKeyDown);
    };
  }, [enabled, rootRef]);
}
